import { NgClass } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { SpanTooltipComponent } from '@shared/components/span-tooltip/span-tooltip.component';
import { ApiPropertiesFactory } from '@shared/factories/api-properties/api-properties.factory';
import { GlobalCfgFactory } from '@shared/factories/global-cfg/global-cfg.factory';
import { LoaderFactory } from '@shared/factories/loader/loader.factory';
import { AfTranslateDirective } from '@shared/modules/translate/directive/af-translate.directive';
import { AfTranslateFactory } from '@shared/modules/translate/factory/translate.factory';
import { ApiServiceService } from '@shared/services/api-service/api-service.service';
import { CommonUtilsService } from '@shared/services/common-utils/common-utils.service';
import { MultilanguageService } from '@shared/services/multilanguage-service/multilanguage.service';
import { cloneDeep } from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { CustomizeFieldsComponent } from '../customize-fields/customize-fields.component';
import { FormsModule } from '@angular/forms';
import { ModalServiceService } from '@shared/services/modal-service/modal-service.service';

@Component({
  selector: 'form-translations-modal',
  standalone: true,
  imports: [
    AfTranslateDirective,
    NgClass,
    OverlayPanelModule,
    SpanTooltipComponent,
    CustomizeFieldsComponent,
    FormsModule
  ],
  templateUrl: './form-translations-modal.component.html'
})
export class FormTranslationsModalComponent implements OnInit {
  skin: string = this.globalCfg.skin;
  configuration: any = this.dialogConfig.data;
  openLanguagesDropdown: boolean = false;
  formTranslations: any = {
    showWarning: true,
    translationsValid: true,
    translationLanguagesErrors: [],
    typeView: "translationsViewNoEditable",
    previewMode: true,
    hasChange: false,
    formTranslationsDataTreatment: {
      description: '',
      label: ''
    }
  };

  @ViewChild('groupFormInput') groupFormInput: ElementRef;

  constructor(
    private dialogRef: DynamicDialogRef<FormTranslationsModalComponent>,
    private dialogConfig: DynamicDialogConfig,
    private apiService: ApiServiceService,
    private commonUtilsService: CommonUtilsService,
    private translate: AfTranslateFactory,
    private loader: LoaderFactory,
    private globalCfg: GlobalCfgFactory,
    private apiProperties: ApiPropertiesFactory,
    private multilanguageService: MultilanguageService,
    private modalService: ModalServiceService
  ) {
  }

  ngOnInit(): void {
    this.setConfig();
    this.setAvailableLanguages();
    this.setExistingTranslations();
  }

  setConfig(){
    this.formTranslations.fieldContainerDefault = this.configuration.fieldContainer;
    this.formTranslations.fieldContainer = cloneDeep(this.configuration.fieldContainer);
    this.formTranslations.fieldTranslations = this.configuration.fieldTranslations;
    this.formTranslations.conceptInfo = this.configuration.conceptInfo;
  }

  setAvailableLanguages() {
    this.formTranslations.formTranslationsLanguagesApp = this.commonUtilsService.configureAvailableLanguages(this.apiProperties.availableLanguages);
  }

  setExistingTranslations() {
    let that = this.formTranslations;
    let selectedLanguages = [];

    selectedLanguages.push(this.apiProperties.defaultLanguage);
    selectedLanguages = selectedLanguages.concat(this.getLanguagesUsed());
    that.formTranslationsLanguagesSelected = this.commonUtilsService.configureAvailableLanguages(selectedLanguages);

    for (let i = 0; i < that.formTranslationsLanguagesSelected.length; i++) {
      let language = that.formTranslationsLanguagesSelected[i];
      for (let fieldTranslation of that.fieldTranslations) {
        if (fieldTranslation.language === language.listValueId) {
          language.isTranslated = fieldTranslation.isTranslated;
          break;
        }
      }
      language.error = false;
      language.indexListLanguage = i;
      language.active = language.listValueId === this.apiProperties.defaultLanguage;
      language.defaultOption = language.listValueId === this.apiProperties.defaultLanguage;
      if (language.defaultOption) {
        this.setActiveLanguage(language);
      }
    }
  }

  getLanguagesUsed() {
    let that = this.formTranslations;
    let languages: any = [];
    for (let translation of that.fieldTranslations) {
      if (translation.isTranslated) {
        languages.push(translation.language);
      }
    }
    return languages;
  }

  setActiveLanguage(language: any) {
    this.formTranslations.activeLanguage = language;
  }

  changeActiveLanguagesSelected(selectedLanguage: any){
    this.saveTranslationData(selectedLanguage);
    this.setActiveLanguage(selectedLanguage);
    this.setFormWithTranslationData();
    this.changeTypeView(selectedLanguage);
    this.multilanguageService.changeActiveLanguagesSelected(selectedLanguage, false, this.formTranslations, 'formTranslations');
  }

  removeActiveLanguage(selectedLanguage: any){
    this.modalService.adviceModal({
      typeModal: 'simple',
      type: 'info',
      text: this.translate.instant('form-translations-modal.delete-translation'),
      typeButtons: 'cancelRemove'
    }).subscribe((result: any) => {
      if (result.result === 'ok') {
        this.multilanguageService.removeActiveLanguage(selectedLanguage, this.formTranslations, 'formTranslations');
        this.findAndSetActiveLanguage();
        this.setFormWithTranslationData();
        this.restoreTranslation(selectedLanguage);
      }
    });
  }

  selectLanguage(selectedLanguage: any, dropdown: any){
    this.saveTranslationData(selectedLanguage);
    this.setActiveLanguage(selectedLanguage);
    this.setFormWithTranslationData();
    this.changeTypeView(selectedLanguage);
    this.multilanguageService.selectLanguage(selectedLanguage.listValueId, this.formTranslations, 'formTranslations');
    dropdown.hide();
  }

  saveTranslationData(language: any) {
    let that = this.formTranslations;
    if (that.activeLanguage.defaultOption) {
      return;
    }
    let fieldTranslation = this.findFieldTranslation(that.activeLanguage.listValueId);
    fieldTranslation.translatedName = that.fieldContainer.name !== fieldTranslation.originalName ? that.fieldContainer.name : null;
    for (let i = 0; i < fieldTranslation.fields.length; i++) {
      let containerField = that.fieldContainer.fields[i];
      let translationField = fieldTranslation.fields[i];
      translationField.translatedLabel = containerField.label !== translationField.originalLabel ? containerField.label : null;
      translationField.translatedExtendedLabel = containerField.extendedLabel !== translationField.originalExtendedLabel ? containerField.extendedLabel : null;
      translationField.translatedDescription = containerField.description !== translationField.originalDescription ? containerField.description : null;
      translationField.translatedDefaultValue = containerField.defaultValue !== translationField.originalDefaultValue ? containerField.defaultValue : null;
      translationField.translatedVoiceRecordOkText = containerField.voiceRecordOkText !== translationField.originalVoiceRecordOkText ? containerField.voiceRecordOkText : null;
      for (let x = 0; x < translationField.listValues.length; x++) {
        translationField.listValues[x].translatedText = containerField.listValues[x].text !== translationField.listValues[x].originalText ? containerField.listValues[x].text : null;
      }
    }
  }

  setFormWithTranslationData() {
    const checkTranslationIsEmpty = (value: any, container: any) => {
      if (value !== null && !value.length) {
        container.error = true;
        container.editableName = true;
      } else {
        container.error = false;
        container.editableName = false;
      }
    }
    const disableEmptyDefaultFields = (field: any, value: any, key: any) => {
      if (!value) {
        field.push(key)
      }
    }
    let that = this.formTranslations;
    if (that.activeLanguage.defaultOption) {
      that.fieldContainer = cloneDeep(that.fieldContainerDefault);
      return;
    }
    let container = cloneDeep(that.fieldContainer);
    let translation = this.findFieldTranslation(that.activeLanguage.listValueId);
    checkTranslationIsEmpty(translation.translatedName, container);
    container.name = translation.translatedName ?? translation.originalName;
    for (let i = 0; i < container.fields.length; i++) {
      let containerField = container.fields[i];
      let translationField = translation.fields[i];
      containerField.disabledTranslations = [];
      let listTranslatedValues = new Set();
      checkTranslationIsEmpty(translationField.translatedLabel, containerField);
      containerField.open = false;
      containerField.label = translationField.translatedLabel ?? translationField.originalLabel;
      containerField.extendedLabel = translationField.translatedExtendedLabel ?? translationField.originalExtendedLabel;
      disableEmptyDefaultFields(containerField.disabledTranslations, translationField.originalExtendedLabel, 'extendedLabel');
      containerField.description = translationField.translatedDescription ?? translationField.originalDescription;
      disableEmptyDefaultFields(containerField.disabledTranslations, translationField.originalDescription, 'description');
      containerField.defaultValue = translationField.translatedDefaultValue ?? translationField.originalDefaultValue;
      disableEmptyDefaultFields(containerField.disabledTranslations, translationField.originalDefaultValue, 'defaultValue');
      containerField.voiceRecordOkText = translationField.translatedVoiceRecordOkText ?? translationField.originalVoiceRecordOkText;
      disableEmptyDefaultFields(containerField.disabledTranslations, translationField.originalVoiceRecordOkText, 'voiceRecordOk');
      for (let x = 0; x < containerField.listValues.length; x++) {
        let containerListValue = containerField.listValues[x];
        let translationListValue = translationField.listValues[x];
        delete containerListValue.error;
        delete containerListValue.errorType;
        if (translationListValue.translatedText !== null) {
          if (!translationListValue.translatedText.length) {
            containerListValue.error = true;
            containerListValue.errorType = 'generic';
            containerField.open = true;
          }
        }
        containerListValue.text = translationListValue.translatedText ?? translationListValue.originalText;
        if (listTranslatedValues.has(containerListValue.text)) {
          containerListValue.error = true;
          containerListValue.errorType = 'duplicateName';
          containerField.open = true;
        }
        listTranslatedValues.add(containerListValue.text);
      }
    }
    that.fieldContainer = container;
    that.hasChange = true;
  }

  restoreTranslation(language: any) {
    for (let fieldTranslation of this.formTranslations.fieldTranslations) {
      if (fieldTranslation.language === language.listValueId) {
        fieldTranslation.translatedName = null;
        for (let field of fieldTranslation.fields) {
          field.translatedLabel = null;
          field.translatedExtendedLabel = null;
          field.translatedDescription = null;
          field.translatedDefaultValue = null;
          field.translatedVoiceRecordOkText = null;
          for (let listValue of field.listValues) {
            listValue.translatedText = null;
          }
        }
        break;
      }
    }
  }

  checkAndValidateTranslations(): boolean {
    let that = this.formTranslations;
    that.translationsValid = true;
    const checkValueIsEmpty = (translatedValue: any, language: any) => {
      if (translatedValue !== null && !translatedValue.length) {
        language.error = true;
        return true;
      }
      return false;
    }
    for (let languageSelected of that.formTranslationsLanguagesSelected) {
      for (let fieldTranslation of that.fieldTranslations) {
        if (languageSelected.listValueId !== fieldTranslation.language || languageSelected.listValueId === this.apiProperties.defaultLanguage) {
          continue;
        }
        languageSelected.error = false;
        checkValueIsEmpty(fieldTranslation.translatedName, languageSelected);
        for (let i = 0; i < fieldTranslation.fields.length; i++) {
          let containerField = that.fieldContainer.fields[i];
          let translationField = fieldTranslation.fields[i];
          checkValueIsEmpty(translationField.translatedLabel, languageSelected);
          if (containerField.fieldTypeId === 'list' && !containerField.numericalScale) {
            let listValueTexts = new Set();
            for (let x = 0; x < translationField.listValues.length; x++) {
              let translationListValue = translationField.listValues[x];
              if (!checkValueIsEmpty(translationListValue.translatedText, languageSelected)) {
                if (listValueTexts.has(translationListValue.translatedText)) {
                  languageSelected.error = true;
                }
                if (translationListValue.translatedText !== null) {
                  listValueTexts.add(translationListValue.translatedText);
                }
              }
            }
          }
        }
        if (languageSelected.error) {
          that.translationsValid = false;
        }
      }
    }
    return that.translationsValid;
  }

  changeTypeView(language: any) {
    let that = this.formTranslations;
    if (language.defaultOption) {
      that.typeView = 'translationsViewNoEditable'
      that.previewMode = true;
    } else {
      that.typeView = 'translationsViewEditable'
      that.previewMode = false;
    }
  }

  findFieldTranslation(languageValueId: string) {
    for (let fieldTranslation of this.formTranslations.fieldTranslations) {
      if (fieldTranslation.language === languageValueId) {
        return fieldTranslation;
      }
    }
  }

  findAndSetActiveLanguage() {
    for (let language of this.formTranslations.formTranslationsLanguagesSelected) {
      if (language.active) {
        this.setActiveLanguage(language);
        break;
      }
    }
  }

  notSelectLanguage(language: any){
    return this.multilanguageService.notSelectLanguage(language, this.formTranslations, 'formTranslations');
  }

  fieldContainerNameChange(container: any){
    if (!container.name) {
/*       this.groupFormInput.nativeElement.focus(); */
      container.error = true;
    } else {
      container.error = false;
      container.editableName = false;
    }
  }

  showField(e: any) {
    if (e.action === 'open') {
      let field: any = e.field;
      if (!field.open) {
        for (let field of this.formTranslations.fieldContainer.fields) {
          field.open = false;
          field.editableName = false;
        }
        if (this.formTranslations.typeView === 'translationsViewEditable') {
          field.editableName = true;
        }
      } else {
        field.editableName = false;
      }
      field.open = !field.open;
    }
  }

  launchAction(e: any) {
    switch(e.action){
      case 'open':
        this.showField(e);
        break;
    }
  }

  handleDismiss() {
    this.dialogRef.close({
      result: 'cancel'
    });
  }

  handleAccept() {
    let that = this.formTranslations;
    this.saveTranslationData(that.activeLanguage);
    if (this.checkAndValidateTranslations()) {
      this.loader.openLoader("save-form-translations");
      this.apiService.patch('fieldcontainers/translations/fieldcontainer', that.fieldContainer.fieldContainerId, that.fieldTranslations)
        .then(
          (data: any) => {
            this.loader.closeLoader("save-form-translations");
            this.dialogRef.close({
              result: 'ok',
              translations: that.fieldTranslations
            });
          },
          (errorData: any) => {
            console.log(errorData)
            this.loader.closeLoader("save-form-translations");
          }
        )
    } else {
      this.modalService.adviceModal({
        typeModal: 'simple',
        type: 'error',
        typeButtons: 'accept',
        text: this.translate.instant('form-translations-modal.translation-error')
      });
      this.setFormWithTranslationData();
    }
  }
}
